import { Slider } from 'antd'
import { useState } from 'react'
import MixpanelTracker from '../../../services/track/mixpanel'
import { PageLang } from '../pageLang'

type CategoryKey = 'small' | 'medium' | 'suv' | 'premium'

interface CategoryConfig {
  price: number
  examples: string[]
}

const CATEGORIES: Record<CategoryKey, CategoryConfig> = {
  small: {
    price: 950,
    examples: ['Toyota Yaris', 'Nissan Almera', 'Mazda 2', 'Honda Brio'],
  },
  medium: {
    price: 1300,
    examples: ['Honda City', 'Toyota Corolla Altis', 'Mazda 3', 'Nissan Almera Turbo'],
  },
  suv: {
    price: 1600,
    examples: ['BYD Atto 3', 'Honda HR-V', 'Toyota Corolla Cross', 'MG ZS EV'],
  },
  premium: {
    price: 4000,
    examples: ['Mercedes-Benz C200', 'BMW 320i', 'Audi A4', 'Volvo S60'],
  },
}

const MIN_DAYS = 5
const MAX_DAYS = 30
const DEFAULT_DAYS = 15

const CAR_ICON =
  'https://ts-upload.sgp1.digitaloceanspaces.com/cms/1faTxUB9JFmvrP12VvCwdLiGKt687ZfRehxg5kxpdUZV5Kczgfvoo3NsAhGXuU93xAWpLwgXKery4tuen4te1UB5.png'

const CONTENT: Record<
  PageLang,
  {
    eyebrow: string
    headline: string
    subtext: string
    bannerTitle: string
    bannerSubtext: string
    bannerBtn: string
    chooseCarType: string
    categoryLabels: Record<CategoryKey, string>
    perDay: string
    selectRentalDays: string
    rentalDaysPerMonth: string
    daysUnit: string
    atLeastDays: (n: number) => string
    maxDays: (n: number) => string
    estimatedIncomePerMonth: string
    disclaimer: string
    planNote: string
  }
> = {
  EN: {
    eyebrow: 'Potential earnings',
    headline: "See your car's earning potential",
    subtext: 'Explore our average pricing or calculate your potential income.',
    bannerTitle: 'Want a precise estimate?',
    bannerSubtext: 'Enter your car details to see your personalized monthly projection.',
    bannerBtn: 'Calculate for my cars →',
    chooseCarType: 'Choose your car type',
    categoryLabels: { small: 'Small', medium: 'Medium', suv: 'SUV', premium: 'Premium' },
    perDay: 'per day',
    selectRentalDays: 'Select estimated rental days',
    rentalDaysPerMonth: 'Rental days per month',
    daysUnit: 'days',
    atLeastDays: (n) => `At least ${n} days`,
    maxDays: (n) => `Maximum ${n} days`,
    estimatedIncomePerMonth: 'Estimated income per month',
    disclaimer:
      'Estimate only, based on typical booking patterns for similar cars on our platform. Actual earnings may vary by location, condition, and demand.',
    planNote: 'Earnings may vary depending on the selected plan.',
  },
  TH: {
    eyebrow: 'รายได้ที่คาดว่าจะได้รับ',
    headline: 'ดูรายได้ที่รถของคุณสามารถสร้างได้',
    subtext: 'สำรวจราคาเฉลี่ยของเรา หรือคำนวณรายได้ที่คาดว่าจะได้รับ',
    bannerTitle: 'ต้องการประเมินรายได้ที่แม่นยำขึ้นไหม?',
    bannerSubtext: 'กรอกข้อมูลรถของคุณเพื่อดูรายได้โดยประมาณต่อเดือนที่เหมาะกับคุณ',
    bannerBtn: 'คำนวณรายได้ของฉัน →',
    chooseCarType: 'เลือกประเภทรถของคุณ',
    categoryLabels: { small: 'เล็ก', medium: 'กลาง', suv: 'SUV', premium: 'พรีเมียม' },
    perDay: 'ต่อวัน',
    selectRentalDays: 'เลือกจำนวนวันเช่าโดยประมาณ',
    rentalDaysPerMonth: 'จำนวนวันเช่าต่อเดือน',
    daysUnit: 'วัน',
    atLeastDays: (n) => `อย่างน้อย ${n} วัน`,
    maxDays: (n) => `สูงสุด ${n} วัน`,
    estimatedIncomePerMonth: 'รายได้โดยประมาณต่อเดือน',
    disclaimer:
      'เป็นเพียงการประมาณการจากรูปแบบการจองทั่วไปของรถที่คล้ายกันบนแพลตฟอร์มของเรา รายได้จริงอาจแตกต่างกันไปตามทำเล สภาพรถ และความต้องการ',
    planNote: 'รายได้อาจแตกต่างกันไปตามแผนที่คุณเลือก',
  },
}

export interface EarningPotentialSectionProps {
  lang: PageLang
  onOpenCarEstimateModal: () => void
}

export const EarningPotentialSection = ({
  lang,
  onOpenCarEstimateModal,
}: EarningPotentialSectionProps) => {
  const t = CONTENT[lang]
  const [category, setCategory] = useState<CategoryKey>('small')
  const [rentalDays, setRentalDays] = useState<number>(DEFAULT_DAYS)

  const dailyRate = CATEGORIES[category].price
  const estimatedIncome = Math.round(dailyRate * rentalDays)

  return (
    <div style={{ padding: '32px 16px 8px' }} id="earning-potential">
      <div
        style={{
          display: 'inline-block',
          backgroundColor: '#EAF1FF',
          color: '#0048B3',
          fontSize: '12px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '12px',
          marginBottom: '12px',
        }}
      >
        {t.eyebrow}
      </div>
      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#212427', margin: '0 0 6px' }}>
        {t.headline}
      </h2>
      <p style={{ fontSize: '13px', color: '#667085', lineHeight: '1.5', marginBottom: '20px' }}>
        {t.subtext}
      </p>

      <div
        style={{
          background: 'linear-gradient(155deg, #0058D6 0%, #0048B3 45%, #00206B 100%)',
          borderRadius: '14px',
          padding: '18px',
          marginBottom: '20px',
        }}
      >
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#fff', marginBottom: '4px' }}>
          {t.bannerTitle}
        </div>
        <div style={{ fontSize: '12.5px', color: '#C7DBFF', marginBottom: '14px' }}>
          {t.bannerSubtext}
        </div>
        <button
          onClick={() => {
            MixpanelTracker.track('Login Page Precise Estimate Click')
            onOpenCarEstimateModal()
          }}
          style={{
            width: '100%',
            height: '44px',
            backgroundColor: '#fff',
            border: 'none',
            borderRadius: '22px',
            color: '#0048B3',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t.bannerBtn}
        </button>
      </div>

      <div style={{ fontSize: '13px', fontWeight: 600, color: '#212427', marginBottom: '10px' }}>
        {t.chooseCarType}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        {(Object.keys(CATEGORIES) as CategoryKey[]).map((key) => {
          const isActive = category === key
          return (
            <div
              key={key}
              onClick={() => {
                MixpanelTracker.track('Login Page Car Type Select', { type: key })
                setCategory(key)
              }}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '8px 4px',
                borderRadius: '10px',
                border: isActive ? '1.5px solid #0048B3' : '1.5px solid transparent',
                backgroundColor: isActive ? '#fff' : '#F4F5F9',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: isActive ? '#0048B3' : '#212427',
                }}
              >
                {t.categoryLabels[key]}
              </div>
              <div style={{ fontSize: '11px', color: '#8C97A7', marginTop: '2px' }}>
                ฿{CATEGORIES[key].price.toLocaleString()} {t.perDay}
              </div>
            </div>
          )
        })}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#F4F5F9',
          borderRadius: '10px',
          padding: '10px 12px',
          fontSize: '12.5px',
          color: '#667085',
          marginBottom: '20px',
        }}
      >
        <img src={CAR_ICON} alt="" style={{ width: '16px', height: '16px' }} />
        {CATEGORIES[category].examples.join(' · ')}
      </div>

      <div style={{ fontSize: '13px', fontWeight: 600, color: '#212427', marginBottom: '10px' }}>
        {t.selectRentalDays}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: '13px',
          color: '#667085',
          marginBottom: '4px',
        }}
      >
        <span>{t.rentalDaysPerMonth}</span>
        <span style={{ fontSize: '15px', fontWeight: 700, color: '#0048B3' }}>
          {rentalDays} {t.daysUnit}
        </span>
      </div>
      <div style={{ padding: '0 4px', marginBottom: '4px' }}>
        <Slider
          min={MIN_DAYS}
          max={MAX_DAYS}
          value={rentalDays}
          onChange={(value) => setRentalDays(value as number)}
          tooltip={{ formatter: (v) => `${v} ${t.daysUnit}` }}
          trackStyle={{ backgroundColor: '#0048B3' }}
          handleStyle={{ borderColor: '#0048B3' }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: '#8C97A7',
          marginBottom: '20px',
        }}
      >
        <span>{t.atLeastDays(MIN_DAYS)}</span>
        <span>{t.maxDays(MAX_DAYS)}</span>
      </div>

      <div
        style={{
          backgroundColor: '#F4F5F9',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: '13px',
            color: '#212427',
          }}
        >
          <span>{t.estimatedIncomePerMonth}</span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: '#0048B3' }}>
            ฿{estimatedIncome.toLocaleString()}
          </span>
        </div>
      </div>

      <p style={{ fontSize: '11px', color: '#8C97A7', lineHeight: '1.5', marginBottom: '6px' }}>
        {t.disclaimer}
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '6px',
          fontSize: '11px',
          color: '#8C97A7',
        }}
      >
        <span>ⓘ</span>
        <span>{t.planNote}</span>
      </div>
    </div>
  )
}

export default EarningPotentialSection
